import { isMembershipProduct, isMerchandiseProduct } from "common";
import {
    OrFilter,
    Product,
    PurchaseItemObject,
    PurchaseRule,
    PurchaseRuleFilterObject,
    PurchaseRuleLink,
    PurchaseRuleLinkEntityObject,
    RuleFilterItem,
} from "types";

interface ProductSelectionState {
    experiences: boolean;
    memberships: boolean;
    merchandise: boolean;
}

interface ProductIds {
    selectedExperienceIds: string[];
    selectedMembershipIds: string[];
    selectedMerchandiseIds: string[];
}

const getProductSelectionState = (
    filters: RuleFilterItem[],
    purchaseRuleLink?: PurchaseRuleLink | null,
): ProductSelectionState => {
    const isAllSelected: ProductSelectionState = {
        experiences: false,
        memberships: false,
        merchandise: false,
    };

    for (const filter of filters) {
        const { object } = filter;

        if (
            object === PurchaseRuleFilterObject.IN_FILTER &&
            filter.field === "productScopeForTaxFees" &&
            Array.isArray(filter.operand) &&
            filter.operand?.every((product: string | null) =>
                ["experience", "membership", "ware"].includes(product ?? ""),
            )
        ) {
            if (filter.operand.includes("experience")) {
                isAllSelected.experiences = true;
            }
            if (filter.operand.includes("membership")) {
                isAllSelected.memberships = true;
            }
            if (filter.operand.includes("ware")) {
                isAllSelected.merchandise = true;
            }
        }

        if (object === PurchaseRuleFilterObject.EQUALS_FILTER && filter.field === "productScopeForTaxFees") {
            switch (filter.operand) {
                case "experience":
                    isAllSelected.experiences = true;
                    break;
                case "membership":
                    isAllSelected.memberships = true;
                    break;
                case "ware":
                    isAllSelected.merchandise = true;
                    break;
            }
        }

        // Handle migrated seller's tax fees with objectName filter
        if (
            object === PurchaseRuleFilterObject.IN_FILTER &&
            purchaseRuleLink?.entities[0]?.object === PurchaseRuleLinkEntityObject.SELLER &&
            filter.field === "objectName"
        ) {
            if (filter.operand?.includes(PurchaseItemObject.EXPERIENCE)) {
                isAllSelected.experiences = true;
            }
            if (filter.operand?.includes(PurchaseItemObject.MEMBERSHIP)) {
                isAllSelected.memberships = true;
            }
        }
    }

    return isAllSelected;
};

const extractSpecificProductIdsFromFilter = (
    filters: RuleFilterItem[],
    isAllSelected: ProductSelectionState,
    products: Product[],
): ProductIds => {
    const productIds: ProductIds = {
        selectedExperienceIds: [],
        selectedMembershipIds: [],
        selectedMerchandiseIds: [],
    };

    for (const filter of filters) {
        if (
            filter.object === PurchaseRuleFilterObject.IN_FILTER &&
            filter.field === "productForTaxFees.id" &&
            (!isAllSelected.experiences || !isAllSelected.memberships || !isAllSelected.merchandise)
        ) {
            // Get the IDs from the filter
            const ids = filter.operand as ID[];

            // Count how many product types have specific selections
            const specificSelectionCount = [
                !isAllSelected.experiences,
                !isAllSelected.memberships,
                !isAllSelected.merchandise,
            ].filter(Boolean).length;

            // Only one product type has specific selection, so all IDs belong to that type
            if (specificSelectionCount === 1) {
                if (!isAllSelected.experiences) {
                    productIds.selectedExperienceIds = ids;
                } else if (!isAllSelected.memberships) {
                    productIds.selectedMembershipIds = ids;
                } else if (!isAllSelected.merchandise) {
                    productIds.selectedMerchandiseIds = ids;
                }
            } else {
                // When 2+ product types have specific selections, map IDs to their product types
                const productMap = new Map<ID, Product>();
                for (const product of products) {
                    productMap.set(product.id, product);
                }

                for (const id of ids) {
                    const product = productMap.get(id);
                    if (product) {
                        if (isMembershipProduct(product)) {
                            productIds.selectedMembershipIds.push(id);
                        } else if (isMerchandiseProduct(product)) {
                            productIds.selectedMerchandiseIds.push(id);
                        } else {
                            // Experience (default)
                            productIds.selectedExperienceIds.push(id);
                        }
                    }
                }
            }
        }
    }

    return productIds;
};

/**
 * Extracts product IDs from PurchaseRuleLink entities.
 * Only relies on entities when ALL three product types have specific selections (none are "all").
 */
const extractProductIdsFromLink = (
    purchaseRuleLink: PurchaseRuleLink | null | undefined,
    isAllSelected: ProductSelectionState,
    existingIds: ProductIds,
): ProductIds => {
    const productIds = { ...existingIds };

    if (!purchaseRuleLink) {
        return productIds;
    }

    // Get from PurchaseRuleLink entities when types have specific selections and IDs weren't mapped from products
    const hasSpecificSelections =
        !isAllSelected.experiences || !isAllSelected.memberships || !isAllSelected.merchandise;

    if (hasSpecificSelections) {
        const experienceIdsFromEntities = purchaseRuleLink.entities
            .filter((entity) => entity.object === PurchaseRuleLinkEntityObject.EXPERIENCE)
            .map((entity) => entity.id);

        const membershipIdsFromEntities = purchaseRuleLink.entities
            .filter((entity) => entity.object === PurchaseRuleLinkEntityObject.MEMBERSHIP)
            .map((entity) => entity.id);

        const merchandiseIdsFromEntities = purchaseRuleLink.entities
            .filter((entity) => entity.object === PurchaseRuleLinkEntityObject.WARE)
            .map((entity) => entity.id);

        if (
            !isAllSelected.experiences &&
            productIds.selectedExperienceIds.length === 0 &&
            experienceIdsFromEntities.length > 0
        ) {
            productIds.selectedExperienceIds = experienceIdsFromEntities;
        }

        if (
            !isAllSelected.memberships &&
            productIds.selectedMembershipIds.length === 0 &&
            membershipIdsFromEntities.length > 0
        ) {
            productIds.selectedMembershipIds = membershipIdsFromEntities;
        }

        if (
            !isAllSelected.merchandise &&
            productIds.selectedMerchandiseIds.length === 0 &&
            merchandiseIdsFromEntities.length > 0
        ) {
            productIds.selectedMerchandiseIds = merchandiseIdsFromEntities;
        }
    }

    return productIds;
};

/*
 * PurchaseRule schema can be of one of two types:
 * 1. Top level "and" filter with "or:productType" and "or:purchaseSource" (This is how UI creates purchase rule from X2 Seller) or,
 * 2. Top level "or:productType" filter (Migration script creates the filter with this schema)
 */
const getPurchaseRuleProductFilters = (purchaseRule?: PurchaseRule | null): OrFilter | null => {
    if (!purchaseRule?.filter?.object || !purchaseRule.filter.filters) {
        return null;
    }

    const isInvalidMigratedPurchaseRule =
        purchaseRule.filter.object === PurchaseRuleFilterObject.OR_FILTER && purchaseRule.filter.filters.length === 0;
    const isInvalidX2PurchaseRule =
        purchaseRule.filter.object === PurchaseRuleFilterObject.AND_FILTER &&
        (purchaseRule.filter.filters.length === 0 ||
            purchaseRule.filter.filters[0].object !== PurchaseRuleFilterObject.OR_FILTER);

    if (isInvalidX2PurchaseRule || isInvalidMigratedPurchaseRule) {
        return null;
    }

    if (purchaseRule.filter.object === PurchaseRuleFilterObject.OR_FILTER) {
        return purchaseRule.filter;
    }

    const filter = purchaseRule.filter?.filters[0];
    /*
     * According to purchase rule schema filter.filters can contain two OR filters.
     * If first one is for the regex match filter then that means there is no Experience and Memebership filter.
     * That means all products are selected so we'll return null.
     */
    if (
        filter?.object === PurchaseRuleFilterObject.OR_FILTER &&
        filter?.filters?.[0]?.object !== PurchaseRuleFilterObject.REGEX_MATCH_FILTER
    ) {
        return filter;
    }

    return null;
};

/**
 * Extracts selected product items (experiences, memberships, merchandise) from purchase rule filters.
 * 1. Check productScopeForTaxFees filter to determine which product types are set to "all"
 * 2. For product types NOT set to "all", get specific IDs from:
 *    a. productForTaxFees.id IN_FILTER (when exactly 1 product type has specific selections)
 *    b. productForTaxFees.id IN_FILTER with product type mapping (when 2 product types have specific selections)
 *    c. PurchaseRuleLink entities (when ALL 3 product types have specific selections and none are "all")
 */
export const getSelectedItems = (
    purchaseRule: PurchaseRule | null,
    purchaseRuleLink?: PurchaseRuleLink | null,
    products: Product[] = [],
) => {
    // This is for the edge cases when no product is selected
    if (purchaseRuleLink && purchaseRuleLink.entities?.length === 0) {
        return {
            isAllSelected: { experiences: false, memberships: false, merchandise: false },
            selectedExperienceIds: [],
            selectedMembershipIds: [],
            selectedMerchandiseIds: [],
        };
    }

    // No filter means all product types are selected
    const purchaseRuleProductFilters: OrFilter | null = getPurchaseRuleProductFilters(purchaseRule);
    if (!purchaseRuleProductFilters) {
        return {
            isAllSelected: { experiences: true, memberships: true, merchandise: true },
            selectedExperienceIds: [],
            selectedMembershipIds: [],
            selectedMerchandiseIds: [],
        };
    }

    // Step 1: Determine which product types are "all"
    const productSelectionState = getProductSelectionState(purchaseRuleProductFilters.filters, purchaseRuleLink);

    // Step 2: Extract specific product IDs from filter, mapping IDs to product types when needed
    const productIdsFromFilter = extractSpecificProductIdsFromFilter(
        purchaseRuleProductFilters.filters,
        productSelectionState,
        products,
    );

    // Step 3: Extract specific product IDs from PurchaseRuleLink entities (only when ALL 3 types are specific)
    const finalProductIds = extractProductIdsFromLink(purchaseRuleLink, productSelectionState, productIdsFromFilter);

    return {
        isAllSelected: productSelectionState,
        ...finalProductIds,
    };
};
